<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';

// The AI button in the reply editor, on Teletel's own engine.
//
// It deliberately owns no answering logic. Each item is a question put to the
// agent copilot, which already reads the open conversation and the company's
// approved knowledge, refuses to invent, meters every call to the company, and
// renders its answer with an "insert into the editor" button. Duplicating any
// of that here would be a second thing to keep correct.
//
// The icon is the copilot's own robot, not a sparkle: the button and the panel
// are one feature, and an agent should recognise them as such.
const { t } = useI18n();
const isOpen = ref(false);
const triggerRef = ref(null);

const items = computed(() => [
  {
    key: 'draft',
    label: t('TELETEL_COPILOT.MENU.DRAFT'),
    icon: 'i-lucide-message-square-quote',
  },
  {
    key: 'summarize',
    label: t('TELETEL_COPILOT.MENU.SUMMARIZE'),
    icon: 'i-lucide-list',
  },
  { key: 'ask', label: t('TELETEL_COPILOT.MENU.ASK'), icon: 'i-lucide-bot' },
]);

// 'ask' carries no question: it opens the panel and leaves the agent to type.
const questionFor = key => {
  if (key === 'draft') return t('TELETEL_COPILOT.PROMPTS.DRAFT');
  if (key === 'summarize') return t('TELETEL_COPILOT.PROMPTS.SUMMARIZE');
  return null;
};

const run = key => {
  isOpen.value = false;
  emitter.emit(BUS_EVENTS.TELETEL_COPILOT_ASK, questionFor(key));
};
</script>

<template>
  <div class="relative">
    <NextButton
      ref="triggerRef"
      ghost
      sm
      icon="i-lucide-bot"
      :title="t('TELETEL_COPILOT.MENU.TRIGGER')"
      :aria-label="t('TELETEL_COPILOT.MENU.TRIGGER')"
      :class="{
        'text-n-blue-11 hover:enabled:!bg-n-alpha-2': !isOpen,
        'text-n-blue-11 bg-n-alpha-2': isOpen,
      }"
      @click="isOpen = !isOpen"
    />
    <DropdownBody
      v-if="isOpen"
      v-on-click-outside="[() => (isOpen = false), { ignore: [triggerRef] }]"
      class="min-w-56 z-50 ltr:right-0 rtl:left-0 bottom-full mb-2 [&>ul]:gap-3 [&>ul]:px-4 [&>ul]:py-3.5"
    >
      <div class="flex flex-col items-start gap-2.5">
        <NextButton
          v-for="item in items"
          :key="item.key"
          :label="item.label"
          :icon="item.icon"
          slate
          link
          sm
          class="hover:!no-underline text-n-slate-12 font-normal text-xs w-full !justify-start"
          @click="run(item.key)"
        />
      </div>
    </DropdownBody>
  </div>
</template>
